/**
 * UsersApi.ts — Façade API du domaine users (Phase D2 Round 1).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 * Aucun import React ni Capacitor. Seul couplage externe autorisé :
 *   @/integrations/supabase/client et @/integrations/supabase/types.
 *
 * Invariants respectés :
 *   4.A.10 — tenant_id jamais dans le body ; RLS auth.uid() côté serveur.
 *   4.A.15 — aucun champ PII loggé (email, phone, display_name).
 *   4.B.2  — zéro import supabase dans src/components/ ou src/pages/.
 *   4.B.4  — 0 any, types stricts dérivés de Database['public']['Tables'].
 *
 * Pattern ADR 0001 — RPC client-callable SECURITY DEFINER :
 *   Les méthodes getMyTenantId(), getMyTenantInfo() et hasRole() appellent
 *   des RPCs SECURITY DEFINER via supabase.rpc(). Ce pattern est autorisé
 *   sous 5 conditions (ADR 0001, docs/adr/0001-finalize-fiscal-sale-rpc-client-callable.md) :
 *     1. La table cible a une RLS qui filtre par tenant_id au niveau ligne. ✓
 *     2. La RPC est SECURITY DEFINER SET search_path TO 'public', 'pg_temp'. ✓
 *     3. La RPC ne lit pas de champs PII non-déjà-publics du caller. ✓
 *     4. La RPC n'a pas besoin d'orchestrer plusieurs HTTP calls externes. ✓
 *     5. Un advisory lock par tenant suffit pour l'atomicité. ✓ (n/a ici, lecture seule)
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, notFoundError, authError, unknownError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import type {
  Profile,
  ProfileUpdate,
  TenantInfo,
  AppRole,
  AccessProfile,
  ListProfilesFilters,
  AdminUser,
  AdminUserUpdate,
  AdminUserApprovalUpdate,
  SessionData,
} from './types';

// ---------------------------------------------------------------------------
// Types internes (non-exportés)
// ---------------------------------------------------------------------------

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type AccessProfileRow = Database['public']['Tables']['access_profiles']['Row'];

// ---------------------------------------------------------------------------
// UsersApi
// ---------------------------------------------------------------------------

export const UsersApi = {
  /**
   * Retourne le profil de l'utilisateur authentifié courant.
   *
   * Call site principal : Caisse.tsx L215, L577.
   *
   * PII : Le profil contient display_name (PII). Ne jamais logger le retour.
   * Invariant 4.A.15.
   */
  async getMyProfile(): Promise<Result<Profile, AppError>> {
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) {
      return err(appErrorFromSupabase(authErr, 'UsersApi.getMyProfile'));
    }

    if (!user) {
      return err(authError('no_jwt'));
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getMyProfile'));
    }

    if (!data) {
      return err(notFoundError('profile', user.id));
    }

    return ok(data as ProfileRow);
  },

  /**
   * Retourne l'écran d'accueil choisi par un utilisateur (colonne profiles.home_screen),
   * ou null si aucun profil / aucun choix. Utilisé par la redirection post-login.
   *
   * Lecture tolérante (maybeSingle) : un profil absent renvoie null, pas une erreur,
   * pour aiguiller vers le sélecteur d'écran d'accueil.
   *
   * RLS : un user ne lit que son propre profil (auth.uid()). Invariant 4.A.10.
   */
  async getMyHomeScreen(userId: string): Promise<Result<string | null, AppError>> {
    const { data, error } = await supabase
      .from('profiles')
      .select('home_screen')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getMyHomeScreen'));
    }

    return ok(data?.home_screen ?? null);
  },

  /**
   * Retourne l'identifiant du tenant de l'utilisateur authentifié courant.
   *
   * Pivot multi-tenant référencé 117 fois dans la codebase.
   * Wrapping la RPC get_my_tenant_id() pour isoler l'appel direct Supabase.
   *
   * Pattern ADR 0001 — RPC SECURITY DEFINER client-callable.
   * Conditions ADR 0001 vérifiées : voir JSDoc module.
   *
   * @see docs/adr/0001-finalize-fiscal-sale-rpc-client-callable.md
   */
  async getMyTenantId(): Promise<Result<string, AppError>> {
    const { data, error } = await supabase.rpc('get_my_tenant_id');

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getMyTenantId'));
    }

    if (!data) {
      return err(notFoundError('tenant_id', 'current_user'));
    }

    return ok(data as string);
  },

  /**
   * Retourne les informations publiques du tenant de l'utilisateur courant.
   *
   * Call site principal : Caisse.tsx L1199.
   * La RPC retourne un tableau d'exactement 0 ou 1 ligne.
   *
   * PII : email/phone du tenant sont des PII business. Ne jamais logger.
   * Invariant 4.A.15.
   *
   * Pattern ADR 0001 — RPC SECURITY DEFINER client-callable.
   * Conditions ADR 0001 vérifiées : voir JSDoc module.
   *
   * @see docs/adr/0001-finalize-fiscal-sale-rpc-client-callable.md
   */
  async getMyTenantInfo(): Promise<Result<TenantInfo | null, AppError>> {
    const { data, error } = await supabase.rpc('get_my_tenant_info');

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getMyTenantInfo'));
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      return ok(null);
    }

    return ok(data[0] as TenantInfo);
  },

  /**
   * Retourne le rôle d'un utilisateur donné (RPC get_user_role).
   *
   * Usage : RegisterLockScreen (résolution du rôle après auth staff token),
   * AuthContext (résolution du rôle à la connexion).
   * Pattern ADR 0001 — RPC SECURITY DEFINER client-callable.
   *
   * @param userId user_id cible.
   */
  async getUserRole(userId: string): Promise<Result<string | null, AppError>> {
    const { data, error } = await supabase.rpc('get_user_role', { _user_id: userId });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getUserRole'));
    }

    return ok((data as string | null) ?? null);
  },

  /**
   * Retourne les infos publiques d'un tenant par son slug (RPC get_tenant_by_slug).
   *
   * Page publique (/app/:slug) — appelable sans session. La RPC retourne un
   * tableau de 0 ou 1 ligne.
   *
   * PII : email/phone du tenant sont des PII business. Ne jamais logger. 4.A.15.
   * Pattern ADR 0001 — RPC SECURITY DEFINER client-callable.
   *
   * @param slug slug du tenant.
   */
  async getTenantBySlug(
    slug: string,
  ): Promise<
    Result<
      { id: string; name: string; logo_url: string | null; city: string | null } | null,
      AppError
    >
  > {
    const { data, error } = await supabase.rpc('get_tenant_by_slug', { p_slug: slug });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getTenantBySlug'));
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      return ok(null);
    }

    return ok(data[0]);
  },

  /**
   * Retourne la liste des profils visibles pour l'utilisateur authentifié.
   *
   * RLS s'applique côté serveur — un user ne voit que les profils de son tenant.
   * Un admin/super_admin voit les profils selon sa policy RLS.
   *
   * Usage principal : workflow admin MyPOSStaffConfig, lookup profil par userId.
   *
   * PII : display_name dans les profils. Ne jamais logger la liste.
   * Invariant 4.A.15.
   *
   * @param filters Filtres optionnels (tenantId, userId, role).
   */
  async listProfiles(filters?: ListProfilesFilters): Promise<Result<Profile[], AppError>> {
    let query = supabase.from('profiles').select('*');

    if (filters?.tenantId !== undefined) {
      query = query.eq('tenant_id', filters.tenantId);
    }

    if (filters?.userId !== undefined) {
      query = query.eq('user_id', filters.userId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.listProfiles'));
    }

    return ok((data ?? []) as ProfileRow[]);
  },

  /**
   * Met à jour le profil d'un utilisateur (champs non-PII uniquement).
   *
   * Seuls les champs explicitement passés sont mis à jour.
   * RLS s'applique côté serveur — un user ne peut modifier que son propre profil.
   *
   * Note : ne pas passer display_name/email dans les logs d'erreur.
   * Invariant 4.A.15.
   *
   * @param userId UUID de l'utilisateur cible.
   * @param updates Champs à mettre à jour.
   */
  async updateProfile(userId: string, updates: ProfileUpdate): Promise<Result<Profile, AppError>> {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', userId)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.updateProfile'));
    }

    if (!data) {
      return err(notFoundError('profile', userId));
    }

    return ok(data as ProfileRow);
  },

  /**
   * Vérifie si un utilisateur possède un rôle donné.
   *
   * Pattern ADR 0001 — RPC SECURITY DEFINER client-callable.
   * Conditions ADR 0001 vérifiées : voir JSDoc module.
   *
   * La RPC has_role() prend _user_id en paramètre explicite.
   * Si userId n'est pas fourni, on résout l'utilisateur courant via auth.getUser().
   *
   * @see docs/adr/0001-finalize-fiscal-sale-rpc-client-callable.md
   *
   * @param role  Rôle à vérifier.
   * @param userId UUID de l'utilisateur (optionnel — par défaut : utilisateur courant).
   */
  async hasRole(role: AppRole, userId?: string): Promise<Result<boolean, AppError>> {
    let resolvedUserId = userId;

    if (!resolvedUserId) {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr) {
        return err(appErrorFromSupabase(authErr, 'UsersApi.hasRole'));
      }

      if (!user) {
        return err(authError('no_jwt'));
      }

      resolvedUserId = user.id;
    }

    const { data, error } = await supabase.rpc('has_role', {
      _role: role,
      _user_id: resolvedUserId,
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.hasRole'));
    }

    if (data === null || data === undefined) {
      return err(unknownError('has_role returned null'));
    }

    return ok(data as boolean);
  },

  /**
   * Retourne le profil d'accès granulaire par son identifiant.
   *
   * Utilisé pour les contrôles de permissions fins (perm_caisse, perm_rapports…).
   * RLS s'applique côté serveur.
   *
   * @param accessProfileId UUID du profil d'accès.
   */
  async getAccessProfile(accessProfileId: string): Promise<Result<AccessProfile, AppError>> {
    const { data, error } = await supabase
      .from('access_profiles')
      .select('*')
      .eq('id', accessProfileId)
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getAccessProfile'));
    }

    if (!data) {
      return err(notFoundError('access_profile', accessProfileId));
    }

    return ok(data as AccessProfileRow);
  },

  // ---------------------------------------------------------------------------
  // Méthodes admin — EF admin-users (Phase D4c)
  // ---------------------------------------------------------------------------

  /**
   * Liste tous les utilisateurs — réservé aux super_admin.
   *
   * Appelle l'EF admin-users en GET ou action=list (avec fallback RPC get_admin_user_list
   * absent ici — préférer l'EF qui retourne les emails via auth.admin).
   *
   * Call site : AdminPortal.tsx:516 — fetchClients (branche EF).
   *
   * PII : email, display_name dans les résultats. Ne jamais logger la liste.
   * Invariant 4.A.15 / 4.A.17.
   */
  async adminListUsers(): Promise<Result<AdminUser[], AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'list' },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.adminListUsers'));
    }

    const d = data as { users?: AdminUser[]; error?: string } | null;

    if (d?.error) {
      return err({ type: 'supabase', operation: 'UsersApi.adminListUsers', message: d.error });
    }

    return ok((d?.users ?? []) as AdminUser[]);
  },

  /**
   * Met à jour un utilisateur via l'EF admin-users action=update.
   *
   * Peut mettre à jour : display_name, role, email, approved,
   * subscription_status, subscription_plan, tenant_id.
   * L'EF synchonise subscription_plan au tenant si fourni.
   *
   * Call site : AdminPortal.tsx:614 — handleUpdateClient.
   *
   * PII : email, display_name. Invariant 4.A.15.
   * Invariant 4.A.17 : réservé super_admin, vérifié côté EF.
   *
   * @param updates  Données de mise à jour (user_id obligatoire).
   */
  async adminUpdateUser(updates: AdminUserUpdate): Promise<Result<void, AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'update', ...updates },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.adminUpdateUser'));
    }

    const d = data as { error?: string; success?: boolean } | null;

    if (d?.error) {
      return err({ type: 'supabase', operation: 'UsersApi.adminUpdateUser', message: d.error });
    }

    return ok(undefined);
  },

  /**
   * Bascule l'approbation d'un utilisateur via l'EF admin-users action=update.
   *
   * Sous-opération de adminUpdateUser pour les call sites qui ne touchent
   * que le champ approved (toggle).
   *
   * Call site : AdminPortal.tsx:637 — handleToggleApproval.
   *
   * Invariant 4.A.17 : réservé super_admin, vérifié côté EF.
   *
   * @param update  user_id + approved.
   */
  async adminUpdateApproval(update: AdminUserApprovalUpdate): Promise<Result<void, AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'update', user_id: update.user_id, approved: update.approved },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.adminUpdateApproval'));
    }

    const d = data as { error?: string; success?: boolean } | null;

    if (d?.error) {
      return err({ type: 'supabase', operation: 'UsersApi.adminUpdateApproval', message: d.error });
    }

    return ok(undefined);
  },

  /**
   * Supprime un utilisateur via l'EF admin-users action=delete.
   *
   * L'EF vérifie la protection du compte (is_protected, PROTECTED_EMAILS).
   * Si l'utilisateur est le dernier de son tenant, le tenant est purgé via
   * purge_tenant_safe (soft-delete NF525).
   *
   * Call site : AdminPortal.tsx:658 — handleDeleteClient.
   *
   * Invariant 4.A.8 : soft-delete via EF, NF525 préservé.
   * Invariant 4.A.17 : réservé super_admin, vérifié côté EF.
   *
   * @param userId  UUID de l'utilisateur à supprimer.
   */
  async adminDeleteUser(userId: string): Promise<Result<void, AppError>> {
    const { data, error } = await supabase.functions.invoke('admin-users', {
      method: 'POST',
      body: { action: 'delete', user_id: userId },
    });

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.adminDeleteUser'));
    }

    const d = data as { error?: string; success?: boolean } | null;

    if (d?.error) {
      return err({ type: 'supabase', operation: 'UsersApi.adminDeleteUser', message: d.error });
    }

    return ok(undefined);
  },

  /**
   * Retourne la session courante (access_token + user_id).
   *
   * Wrapping de supabase.auth.getSession() pour isoler l'appel Supabase côté
   * composant. Retourne SessionData minimal (access_token pour les appels fetch
   * directs vers les EFs, user_id pour la traçabilité).
   *
   * Call site : ClotureComptable.tsx:245 — appel fetch vers accountant-send.
   *
   * Invariant 4.A.15 : ne jamais logger access_token (équivalent JWT).
   * Invariant 4.B.2 : remplace supabase.auth.getSession() dans les composants.
   */
  async getSession(): Promise<Result<SessionData, AppError>> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      return err(appErrorFromSupabase(error, 'UsersApi.getSession'));
    }

    return ok({
      access_token: session?.access_token ?? null,
      user_id: session?.user?.id ?? null,
    });
  },

  // ---------------------------------------------------------------------------
  // Méthode adminUpdateUserById — BLOQUÉE (EF manquante)
  // ---------------------------------------------------------------------------
  //
  // CreateClientWizard.tsx:263 appelle supabase.auth.admin?.updateUserById?.(userId, {...})
  // pour mettre à jour user_metadata.full_name côté auth.
  //
  // L'EF admin-users gère déjà ce cas de manière indirecte via action=update
  // (mise à jour de l'email via auth.admin.updateUserById L191), mais NE couvre PAS
  // la mise à jour de user_metadata.full_name isolément.
  //
  // Décision Phase D4c : NE PAS créer d'EF dédiée — le call site existant
  // (supabase.auth.admin?.updateUserById?.) est un appel côté client qui utilise
  // l'API admin Supabase directement, ce qui nécessite le service_role côté serveur.
  //
  // Chemin de migration recommandé : ajouter une action 'update_user_metadata'
  // à l'EF admin-users qui délègue à auth.admin.updateUserById côté Deno.
  // Cette méthode sera alors wrappable ici. À traiter en Phase D5.
  //
  // Statut : BLOQUÉ — EF manquante pour user_metadata.
  //
} as const;
